import type { Dayjs } from "dayjs";
import { Table } from "../hooks/useSchedule";

export interface Schedule {
  id: number;
  user_id: number;
  table_id: number | null;
  schedule_date: string;
  start_time: string;
  end_time: string;
  shift_type: "morning" | "day" | "night";
  status: "scheduled" | "completed" | "canceled";
  notes: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    user_type_id: number;
    location_id: number;
    user_type: {
      id: number;
      name: string;
      icon: string;
      description: string;
    };

    location: {
      id: number;
      name: string;
      code: string;
      address: string;
    };
  };
  table: Table;
}

export interface FilterValues {
  location_id?: number;
  shift_type?: "morning" | "day" | "night";
  status?: "scheduled" | "completed" | "canceled";
  date_range?: [Dayjs, Dayjs];
  user_ids?: number[];
  user_id?: number; // For API filtering
}

// Filter schedules based on provided filters
export const filterSchedules = (
  schedules: Schedule[],
  filters: FilterValues
): Schedule[] => {
  return schedules.filter((schedule) => {
    let matches = true;

    // Filter by location
    if (
      filters.location_id &&
      schedule.user.location_id !== filters.location_id
    ) {
      matches = false;
    }

    // Filter by shift type
    if (filters.shift_type && schedule.shift_type !== filters.shift_type) {
      matches = false;
    }

    // Filter by status
    if (filters.status && schedule.status !== filters.status) {
      matches = false;
    }

    // Note: user_ids filtering is not needed here since user filtering
    // is handled at the API level via user_id parameter

    // Note: date_range filtering is not needed here since date filtering
    // is handled at the API level via start_date and end_date parameters

    return matches;
  });
};

// Group schedules by date for calendar display
export const groupSchedulesByDate = (
  schedules: Schedule[]
): Record<string, Schedule[]> => {
  return schedules.reduce((groups, schedule) => {
    const date = schedule.schedule_date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(schedule);
    return groups;
  }, {} as Record<string, Schedule[]>);
};

// Get badge status for Ant Design Badge component
export const getBadgeStatus = (
  status: string
): "success" | "processing" | "error" | "default" => {
  switch (status) {
    case "completed":
      return "success";
    case "scheduled":
      return "processing";
    case "canceled":
      return "error";
    default:
      return "default";
  }
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Get color for shift type tags
export const getShiftColor = (shift: string): string => {
  switch (shift) {
    case "morning":
      return "blue";
    case "day":
      return "orange";
    case "night":
      return "purple";
    default:
      return "default";
  }
};
